/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintIcon from '@mui/icons-material/Print';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Swal from 'sweetalert2';
import { api } from '../../../api';
import { STATUS_HEX, TEAL, buildPrescriptionPrintUrl, getCurrentUserId } from '../constants';
import CancelDialog from './CancelDialog';
import CreatePrescriptionFromAppointmentDialog from './CreatePrescriptionFromAppointmentDialog';
import RescheduleDialog from './RescheduleDialog';
import ReAppointmentDialog from './ReAppointmentDialog';
import CreateFollowupFromAppointmentDialog from './CreateFollowupFromAppointmentDialog';

// Bare icon button — the label lives only in the hover tooltip, not on the
// button itself. Used for the compact per-row quick actions on List and
// Doctor Serial (the AppointmentDetailDrawer's ActionIcon is a bigger
// labelled pill, which doesn't fit inline on a list row).
function QuickIcon({ title, icon, color, onClick, disabled }) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={e => {
            e.stopPropagation();
            onClick();
          }}
          sx={{
            color,
            width: 26,
            height: 26,
            border: `1px solid ${color}33`,
            bgcolor: `${color}0f`,
            '&:hover': { bgcolor: `${color}22` },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// The full AppointmentDetailDrawer action bar (Check-In/Attend/Complete,
// Reschedule, Cancel, Re-Appointment, Follow-Up, Prescription/Print) as bare
// icons, for inline use on List/Doctor-Serial rows without needing to open
// the detail drawer first.
export default function QuickActionIcons({ appointment, onChanged }) {
  // 'cancel' | 'prescription' | 'reschedule' | 're-appointment' | 'followup'
  const [activeDialog, setActiveDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);

  const runAction = async (title, apiCall) => {
    const confirmResult = await Swal.fire({
      title,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: TEAL,
      confirmButtonText: 'Yes, Confirm',
      cancelButtonText: 'Cancel',
    });
    if (!confirmResult.isConfirmed) return;
    setBusy(true);
    try {
      await apiCall();
      onChanged?.();
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setBusy(false);
    }
  };

  const handleCheckIn = () =>
    runAction('Check in this patient?', () =>
      api.appointment.checkIn(appointment.id, { updated_by: getCurrentUserId() }),
    );
  const handleAttend = () =>
    runAction('Mark as attended by the doctor?', () =>
      api.appointment.attend(appointment.id, { updated_by: getCurrentUserId() }),
    );
  const handleComplete = () =>
    runAction('Mark this appointment as completed?', () =>
      api.appointment.complete(appointment.id, { updated_by: getCurrentUserId() }),
    );

  const handleViewPrescription = async () => {
    if (!appointment?.irm_prescriptions_id) return;
    setOpening(true);
    try {
      const resp = await api.opd.getPrescriptionById(appointment.irm_prescriptions_id);
      const code = resp?.data?.prescription_code;
      if (code) window.open(buildPrescriptionPrintUrl(appointment, code), '_blank', 'noopener,noreferrer');
    } catch {
      // generic error toast already shown by ApiClient
    } finally {
      setOpening(false);
    }
  };

  const closeDialog = () => setActiveDialog(null);
  const onDialogDone = () => {
    closeDialog();
    onChanged?.();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
      {appointment.status === 'SCHEDULED' && (
        <QuickIcon title="Check In" icon={<LoginIcon fontSize="small" />} color={STATUS_HEX.CHECKED_IN} onClick={handleCheckIn} disabled={busy} />
      )}
      {appointment.status === 'CHECKED_IN' && (
        <QuickIcon title="Mark Attended" icon={<MedicalServicesIcon fontSize="small" />} color={STATUS_HEX.ATTENDED} onClick={handleAttend} disabled={busy} />
      )}
      {appointment.status === 'ATTENDED' && (
        <QuickIcon title="Complete" icon={<CheckCircleIcon fontSize="small" />} color={STATUS_HEX.COMPLETED} onClick={handleComplete} disabled={busy} />
      )}
      {appointment.status === 'SCHEDULED' && (
        <QuickIcon title="Reschedule" icon={<EventNoteIcon fontSize="small" />} color={TEAL} onClick={() => setActiveDialog('reschedule')} disabled={busy} />
      )}
      {['ATTENDED', 'COMPLETED'].includes(appointment.status) && !appointment.irm_prescriptions_id && (
        <QuickIcon title="Create Prescription" icon={<ReceiptLongIcon fontSize="small" />} color="#8e24aa" onClick={() => setActiveDialog('prescription')} disabled={busy} />
      )}
      {appointment.irm_prescriptions_id && (
        <QuickIcon
          title={opening ? 'Opening…' : 'View & Print Prescription'}
          icon={<PrintIcon fontSize="small" />}
          color="#8e24aa"
          onClick={handleViewPrescription}
          disabled={opening || busy}
        />
      )}
      <QuickIcon title="Re-Appointment" icon={<EventRepeatIcon fontSize="small" />} color="#5c6bc0" onClick={() => setActiveDialog('re-appointment')} disabled={busy} />
      <QuickIcon title="Follow-Up" icon={<NoteAddIcon fontSize="small" />} color="#455a64" onClick={() => setActiveDialog('followup')} disabled={busy} />
      {!['COMPLETED', 'CANCELLED'].includes(appointment.status) && (
        <QuickIcon title="Cancel" icon={<CancelIcon fontSize="small" />} color={STATUS_HEX.CANCELLED} onClick={() => setActiveDialog('cancel')} disabled={busy} />
      )}

      {activeDialog === 'cancel' && (
        <CancelDialog appointment={appointment} onClose={closeDialog} onDone={onDialogDone} />
      )}
      {activeDialog === 'prescription' && (
        <CreatePrescriptionFromAppointmentDialog appointment={appointment} onClose={closeDialog} onDone={onDialogDone} />
      )}
      {activeDialog === 'reschedule' && (
        <RescheduleDialog appointment={appointment} onClose={closeDialog} onDone={onDialogDone} />
      )}
      {activeDialog === 're-appointment' && (
        <ReAppointmentDialog appointment={appointment} onClose={closeDialog} onDone={onDialogDone} />
      )}
      {activeDialog === 'followup' && (
        <CreateFollowupFromAppointmentDialog appointment={appointment} onClose={closeDialog} onDone={onDialogDone} />
      )}
    </Box>
  );
}
